import { useState, useMemo, useCallback, useEffect } from "react";
import {
    endOfDay,
    endOfHour,
    endOfMinute,
    endOfMonth,
    endOfWeek,
    isBefore,
    isEqual,
    format,
    startOfDay,
    startOfHour,
    startOfMinute,
    startOfMonth,
    startOfWeek,
    max,
    min,
} from "date-fns";
import { usePrevious } from "react-use";
import { unreachableCaseError } from "@edgetier/utilities";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useRemoveUrlParameters } from "hooks-for/urls/use-remove-url-parameters";
import { useToggleUrlParameters } from "hooks-for/urls/use-toggle-url-parameters";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { ISelectedDateArea } from "pages/explore/explore-results/explore-results.types";
import { IInteractionChartFilterSelection } from "types-for/charts/interaction-chart.types";
import { IChartDragOptions, IMouseEventsCallback } from "./use-chart-drag.types";

/**
 * Format active label, used for drawing the reference area on chart
 * @param dateTimeValue Date time
 */
const formatActiveLabel = (dateTimeValue: Date, formatLabel?: string) => {
    if (typeof formatLabel === "string") {
        return format(dateTimeValue, formatLabel);
    }

    return dateTimeValue.getTime();
};

/**
 * Given two dates, figure out which one comes first and return an array with the earlier date being first.
 * @param firstDate
 * @param secondDate
 * @returns An array with the earlier date first and the later date second.
 */
const orderDates = (firstDate: Date, secondDate: Date) => {
    return isBefore(firstDate, secondDate) ? [firstDate, secondDate] : [secondDate, firstDate];
};

/**
 * Custom hook to add interaction (click and drag selection) to a chart using date time as range selection type
 * @param props.defaultValues   Default values for start date time and end date time
 * @param props.prefixOptions   Prefix details for parameters
 * @param props.formatLabel     Active label format only for reference area
 * @param props.timePeriodGroupBy     Selected time period
 */
const useChartDrag = ({ defaultValues, timePeriodGroupBy, prefixOptions, formatLabel }: IChartDragOptions) => {
    const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
    const [mouseStartPosition, setMouseStartPosition] = useState<number | null>(null);
    const currentDateTime = useMemo(() => endOfDay(new Date()), []);
    // Set the first start selection
    const [selectedStartPosition, setSelectedStartPosition] = useState<IInteractionChartFilterSelection>({
        activeLabel: null,
        date: currentDateTime,
    });

    // Get previous selection of start date time and end date time
    // from query parameters
    const { startDateTime, endDateTime } = useInteractionQueryParameters({
        prefix: prefixOptions.prefix,
        isPrefixOptional: prefixOptions.isPrefixOptional,
    });

    // Retrieve from URL the default values for start date time and end date time
    const { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime } = useInteractionQueryParameters({
        defaults: defaultValues,
    });

    // Initialise the filter range with existing selection or use default values
    const [filterDateRange, setFilterDateRange] = useState<
        [IInteractionChartFilterSelection, IInteractionChartFilterSelection]
    >([
        {
            activeLabel: typeof startDateTime === "undefined" ? null : formatActiveLabel(startDateTime, formatLabel),
            date: startDateTime ?? defaultStartDateTime ?? currentDateTime,
        },
        {
            activeLabel:
                typeof endDateTime === "undefined"
                    ? null
                    : formatActiveLabel(
                          timePeriodGroupBy === TimePeriodGroupBy.Hours ? endDateTime : startOfDay(endDateTime),
                          formatLabel
                      ),
            date: endDateTime ?? defaultEndDateTime ?? currentDateTime,
        },
    ]);

    /**
     * Transforms date based on selected time period group
     * @param type Type of date filter: start or end
     * @param dateTime Date time which will be transformed
     * @param timePeriodGroupBy
     */
    const applyFilterDate = useCallback(
        (type: "start" | "end", dateTime: Date, timePeriodGroupBy?: TimePeriodGroupBy) => {
            if (typeof timePeriodGroupBy !== "undefined") {
                switch (timePeriodGroupBy) {
                    case TimePeriodGroupBy.Minutes: {
                        return type === "start" ? startOfMinute(dateTime) : endOfMinute(dateTime);
                    }
                    case TimePeriodGroupBy.Hours: {
                        return type === "start" ? startOfHour(dateTime) : endOfHour(dateTime);
                    }
                    case TimePeriodGroupBy.Days: {
                        return type === "start" ? startOfDay(dateTime) : endOfDay(dateTime);
                    }
                    case TimePeriodGroupBy.Weeks: {
                        return type === "start"
                            ? max([startOfWeek(dateTime, { weekStartsOn: 1 }), defaultStartDateTime!])
                            : min([endOfWeek(dateTime, { weekStartsOn: 1 }), defaultEndDateTime!]);
                    }
                    case TimePeriodGroupBy.Months: {
                        return type === "start"
                            ? max([startOfMonth(dateTime), defaultStartDateTime!])
                            : min([endOfMonth(dateTime), defaultEndDateTime!]);
                    }
                    default: {
                        unreachableCaseError(timePeriodGroupBy);
                        return dateTime;
                    }
                }
            }
            return dateTime;
        },
        [defaultEndDateTime, defaultStartDateTime]
    );

    // Previous filter selected
    const previousFilterRange = usePrevious(filterDateRange);
    const previousStartDate = usePrevious(startDateTime);

    const previousDefaultStartDateTime = usePrevious(defaultStartDateTime);
    const previousDefaultEndDateTime = usePrevious(defaultEndDateTime);

    const removeUrlParameters = useRemoveUrlParameters();
    const toggleUrlParameters = useToggleUrlParameters();

    /**
     * Set the selected date range as query parameters
     * @param selectedDate Reference date area
     */
    const updateUrlDateSelection = useCallback(
        (selectedDate: ISelectedDateArea) => {
            const [selectedStartDateTime, selectedEndDateTime] = selectedDate;

            toggleUrlParameters({
                [`${prefixOptions.prefix}.startDateTime`]: selectedStartDateTime,
                [`${prefixOptions.prefix}.endDateTime`]: selectedEndDateTime,
            });
        },
        [prefixOptions.prefix, toggleUrlParameters]
    );

    /**
     * Remove the selected date from query parameters
     */
    const resetUrlDateSelection = useCallback(() => {
        removeUrlParameters([`${prefixOptions.prefix}.startDateTime`, `${prefixOptions.prefix}.endDateTime`]);
    }, [prefixOptions.prefix, removeUrlParameters]);

    /**
     * Set one side of the filter range.
     * @param chartFilterArea The chart filter area for one side of the filter range.
     * @param side            The side of the filter range being set.
     */
    const setChartFilterArea = (chartFilterArea: IInteractionChartFilterSelection, side: "left" | "right") => {
        if (side === "left") setFilterDateRange(([, right]) => [chartFilterArea, right]);
        else setFilterDateRange(([left]) => [left, chartFilterArea]);
    };

    /**
     * A function that is called once the the user has finished filtering the chart. It makes sure the dates are in
     * the right order and then updates the dates used for filtering the interactions displayed below the chart.
     */
    const onFilterComplete = useCallback(() => {
        const [left, right] = filterDateRange;
        const [startDate, endDate] = orderDates(left.date, right.date);

        updateUrlDateSelection([startDate, endDate]);
    }, [filterDateRange, updateUrlDateSelection]);

    /**
     * Reset the chart so that no selection area is shown and reset the dates so that the interactions below the chart
     * are no longer filtered.
     */
    const resetChartFilter = useCallback(() => {
        setFilterDateRange([
            { activeLabel: null, date: defaultStartDateTime ?? currentDateTime },
            { activeLabel: null, date: defaultEndDateTime ?? currentDateTime },
        ]);

        resetUrlDateSelection();
    }, [currentDateTime, defaultStartDateTime, defaultEndDateTime, resetUrlDateSelection]);

    // Mouse event callbacks
    const mouseEventCallbacks: IMouseEventsCallback = useMemo(() => {
        return {
            onMouseDown: (event: any) => {
                setIsMouseDown(true);
                setMouseStartPosition(event?.activeCoordinate?.x);
                const dateTime = event?.activePayload?.[0]?.payload?.dateTime ?? null;
                setSelectedStartPosition({ activeLabel: event?.activeLabel ?? null, date: dateTime });

                if (dateTime !== null) {
                    const selectedDateTime = new Date(dateTime);

                    setFilterDateRange([
                        {
                            activeLabel: event?.activeLabel ?? null,
                            date: applyFilterDate("start", selectedDateTime, timePeriodGroupBy),
                        },

                        // for a single selection (click) both sides will have the same activeLabel
                        {
                            activeLabel: event?.activeLabel ?? null,
                            // set right side of selection as end of hour or day
                            date: applyFilterDate("end", selectedDateTime, timePeriodGroupBy),
                        },
                    ]);
                }
            },
            onMouseMove: (event: any) => {
                if (isMouseDown) {
                    const dateTime = event?.activePayload?.[0]?.payload?.dateTime ?? null;
                    const mouseMovePosition = event?.activeCoordinate?.x;

                    if (dateTime !== null) {
                        const selectedDateTime = new Date(dateTime);

                        // Using the mouse position relative to initial mouse click
                        // update either left or right side
                        if (mouseMovePosition < mouseStartPosition!) {
                            // Moving on the left, set any new selection to the left and to the right
                            // will always be the point when onClick happened first
                            setFilterDateRange([
                                {
                                    activeLabel: event?.activeLabel ?? null,
                                    date: applyFilterDate("start", selectedDateTime, timePeriodGroupBy),
                                },

                                {
                                    activeLabel: selectedStartPosition.activeLabel,
                                    date: applyFilterDate("end", selectedStartPosition.date, timePeriodGroupBy),
                                },
                            ]);
                        } else {
                            // Moving on the right, set any new selection to the right and to the left
                            // will always be the point when onClick happened first
                            setFilterDateRange([
                                {
                                    activeLabel: selectedStartPosition.activeLabel,
                                    date: applyFilterDate("start", selectedStartPosition.date, timePeriodGroupBy),
                                },

                                {
                                    activeLabel: event?.activeLabel ?? null,
                                    // set side of selection as end of hour or day
                                    date: applyFilterDate("end", selectedDateTime, timePeriodGroupBy),
                                },
                            ]);
                        }
                    }
                }
            },
            onMouseUp: () => {
                setIsMouseDown(false);
                setMouseStartPosition(null);
                setSelectedStartPosition({
                    activeLabel: null,
                    date: currentDateTime,
                });

                // Call the filter complete callback if both active labels are defined. Otherwise, reset.
                if (
                    filterDateRange.every(
                        ({ activeLabel }) => typeof activeLabel === "number" || typeof activeLabel === "string"
                    )
                ) {
                    // Filter range has the same active label for all entries
                    if (filterDateRange.every(({ activeLabel }) => activeLabel === filterDateRange[0].activeLabel)) {
                        // Filter range is the same as previous
                        if (
                            filterDateRange.every(
                                ({ activeLabel }, index) => previousFilterRange![index].activeLabel === activeLabel
                            )
                        ) {
                            resetChartFilter();
                        } else {
                            onFilterComplete();
                        }
                    } else {
                        onFilterComplete();
                    }
                } else {
                    resetChartFilter();
                }
            },
        };
    }, [
        timePeriodGroupBy,
        isMouseDown,
        filterDateRange,
        mouseStartPosition,
        selectedStartPosition.activeLabel,
        selectedStartPosition.date,
        currentDateTime,
        previousFilterRange,
        applyFilterDate,
        resetChartFilter,
        onFilterComplete,
    ]);

    // Reset the chart filter range when start date and end date are changed from filter form
    useEffect(() => {
        if (
            previousDefaultStartDateTime instanceof Date &&
            previousDefaultEndDateTime instanceof Date &&
            (!isEqual(previousDefaultStartDateTime, defaultStartDateTime!) ||
                !isEqual(previousDefaultEndDateTime, defaultEndDateTime!))
        ) {
            resetChartFilter();
        }
    }, [
        defaultEndDateTime,
        defaultStartDateTime,
        previousDefaultEndDateTime,
        previousDefaultStartDateTime,
        resetChartFilter,
    ]);

    // Reset the chart filter range when the selection is removed from URL
    useEffect(() => {
        if (typeof previousStartDate !== "undefined" && typeof startDateTime === "undefined") {
            resetChartFilter();
        }
    }, [previousStartDate, resetChartFilter, startDateTime]);

    return {
        filterDateRange,
        setChartFilterArea,
        onFilterComplete,
        resetChartFilter,
        mouseEventCallbacks,
    };
};

export default useChartDrag;
